/**
 * Annotating the OBJECTS on a shelf crop, not a bare percentage.
 *
 * The percentage was always a derived thing — area of goods over area of shelf — but the
 * old UI made a labeller produce it by eye and type a number. Here SAM 3's objects are
 * drawn on the crop and the labeller corrects them: pull the slider until the coverage
 * looks right, delete what is wrong, drag the points that are off, draw what was missed.
 * The number follows.
 *
 * Coordinates throughout are the UPSCALED crop's pixels — the frame the contours were
 * stored in. The SVG maps them to the screen with a viewBox, so there is one coordinate
 * system and not two.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Loader2, Maximize2, MousePointer2, PenLine, Trash2, Eye, Save, Sparkles, Scissors,
  Undo2, Redo2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import {
  computeShelfMasks, getShelfMasks, recutShelfMasks, saveShelfMasks,
  type ShelfMasks,
} from '@/api/shelfPool';
import {
  baseObjects, buildLadder, clusterObjects, coverageOf, hitTest, nearestVertex, uniqueTo,
  type Contour, type MergedObject,
} from './shelfMaskGeometry';
import UsefulAreaOverlay from './UsefulAreaOverlay';

type Mode = 'view' | 'cursor' | 'pen';
/** How close a click must be to a corner to grab it, in crop pixels. Generous: the crop
 *  is displayed several times smaller than its stored size, so a comfortable screen
 *  radius is a large one here. */
const GRAB_RADIUS = 60;

export default function ShelfMaskEditor(
  { item, onSaved, apply, showUsefulArea = false }: {
    item: { id: number; crop_url: string };
    /** Called with the percentage the SERVER stored, so the grid can update the card
     *  without refetching — a refetch reorders the page and loses the cursor. */
    onSaved?: (percentage: number) => void;
    /** Калибровка «максимума полки» вместо разметки примера. Тот же холст и те же
     *  объекты, но кнопка отдаёт ПОКРЫТИЕ наружу и ничего не пишет в пул: кроп здесь —
     *  референс для калибровки, а не обучающий пример, и проставлять ему `percentage`
     *  значило бы подсунуть в обучение число, которого человек про товар не говорил. */
    apply?: { label: string; onApply: (coverage: number) => void | Promise<void> };
    /** Серый слой полезной площади полки (UsefulAreaOverlay) — переключатель у аннотатора. */
    showUsefulArea?: boolean;
  },
) {
  const { t } = useT();
  const [mode, setMode] = useState<Mode>('cursor');
  const [contours, setContoursRaw] = useState<Contour[]>([]);
  /** Undo/redo over the whole contour set. Small states, a handful per crop — keeping
   *  snapshots is simpler than replaying operations and cannot drift from them. */
  const past = useRef<Contour[][]>([]);
  const future = useRef<Contour[][]>([]);
  const [selected, setSelected] = useState(-1);
  const [rung, setRung] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [queued, setQueued] = useState(false);
  /** A re-cut is queued work like the first pass, but the crop ALREADY has masks, so the
   *  "no masks yet" poll cannot notice it finishing. Remember what the masks looked like
   *  when it was asked for and poll until they are not that any more. */
  const [recutting, setRecutting] = useState(false);
  const recutFrom = useRef<string>('');
  // A presigned crop URL lives six hours. A tab left open across a working day loses its
  // pictures with no error anywhere — which reads as the feature breaking rather than a
  // link expiring, so say which it is.
  const [imgBroken, setImgBroken] = useState(false);
  const [draft, setDraft] = useState<Contour>([]);
  /** Which prompts contribute. A labeller can see what each one found and switch it off,
   *  which is the only honest way to handle a prompt that is right about the word and
   *  wrong about the shelf. */
  const [onPrompts, setOnPrompts] = useState<Set<string>>(new Set());
  const drag = useRef<{ poly: number; vertex: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const { data, isLoading, mutate } = useSWR<ShelfMasks>(
    ['shelf-masks', item.id], () => getShelfMasks(item.id).then(r => r.data),
    // Poll only while the crop has no masks: something is being computed, or nothing is,
    // and either way this stops the moment they arrive.
    { refreshInterval: (d) => ((d && !d.computed) || recutting ? 5000 : 0) },
  );

  /** Identity of the current answer: prompt names and how much each found. Enough to see
   *  a re-cut land, including the case where it lands on nothing again. */
  const signature = (d?: ShelfMasks) =>
    (d?.prompts ?? []).map(p => `${p.prompt}:${(p.contours ?? []).length}`).join('|');
  useEffect(() => {
    if (!recutting || !data) return;
    if (signature(data) !== recutFrom.current) setRecutting(false);
  }, [data, recutting]);

  const size = data?.prompts?.[0];
  const W = size?.width ?? 0;
  const H = size?.height ?? 0;
  // Every detection from every prompt, grouped into physical objects. An object three
  // prompts found is ONE object, not three — which is what made a crop show thirty.
  const raw = useMemo(() => buildLadder(data?.prompts ?? []), [data]);
  const objects = useMemo(() => clusterObjects(raw, W, H), [raw, W, H]);

  // Two or more prompts agreeing is the base: independent words landing on the same
  // pixels is about as much confidence as this gets. A single prompt's finds are a
  // judgement call, so they wait behind that prompt's tick box — off to begin with, on
  // the view that the slider alone is usually enough. Unless NOTHING agreed, in which
  // case the best single prompt becomes the base rather than opening a stocked dessert
  // shelf at 0% — see `baseObjects`.
  const base = useMemo(() => baseObjects(objects, W, H), [objects, W, H]);
  const extras = useMemo(
    () => [...onPrompts].flatMap(p => uniqueTo(objects, p)), [objects, onPrompts]);
  const ladder = useMemo(
    () => [...base, ...extras].sort((a, b) => b.area - a.area) as MergedObject[],
    [base, extras]);

  // Open on what the labeller kept last time; failing that, on the whole ladder, which
  // is SAM's own answer and the thing they are here to correct.
  useEffect(() => {
    setQueued(false); setImgBroken(false);
    past.current = []; future.current = [];
  }, [item.id]);

  // Nothing ticked to begin with: the base is what several prompts agreed on, and the
  // slider over it is usually the whole job. Extras are opt-in.
  useEffect(() => { setOnPrompts(new Set()); }, [item.id]);

  useEffect(() => {
    if (!data) return;
    if (data.contours && data.contours.length) {
      setContours(data.contours as Contour[]);
      setRung(data.contours.length);
    } else {
      setContours(ladder.map(l => l.contour));
      setRung(ladder.length);
    }
    setSelected(-1);
    setDirty(false);
  }, [data, ladder]);

  /** Every deliberate change goes through here so it lands in the history. Dragging a
   *  corner is the exception — it fires per mouse-move, and one undo per pixel would make
   *  Z useless — so a drag records ONE snapshot when it starts. */
  const setContours = useCallback((next: Contour[] | ((p: Contour[]) => Contour[]),
                                   remember = true) => {
    setContoursRaw(prev => {
      const val = typeof next === 'function' ? (next as (p: Contour[]) => Contour[])(prev) : next;
      if (remember) { past.current.push(prev); future.current = []; }
      return val;
    });
  }, []);

  const undo = useCallback(() => {
    setContoursRaw(prev => {
      const last = past.current.pop();
      if (!last) return prev;
      future.current.push(prev);
      return last;
    });
    setSelected(-1);
    setDirty(true);
  }, []);

  const redo = useCallback(() => {
    setContoursRaw(prev => {
      const next = future.current.pop();
      if (!next) return prev;
      past.current.push(prev);
      return next;
    });
    setSelected(-1);
    setDirty(true);
  }, []);

  /** Break a merged object back into the outlines it was made of.
   *
   *  Merging is a display decision; the detections survive it. When a broad outline has
   *  swallowed a bun the labeller wants gone, splitting turns that bun back into its own
   *  object — deletable, draggable — instead of a corner of something too big to edit. */
  const splitSelected = useCallback(() => {
    if (selected < 0) return;
    const obj = ladder.find(l => l.contour === contours[selected]);
    if (!obj || obj.members.length < 2) return;
    setContours(prev => [
      ...prev.slice(0, selected), ...obj.members, ...prev.slice(selected + 1),
    ]);
    setSelected(-1);
    setDirty(true);
  }, [selected, contours, ladder, setContours]);

  const canSplit = selected >= 0
    && (ladder.find(l => l.contour === contours[selected])?.members.length ?? 0) > 1;

  const coverage = useMemo(() => coverageOf(contours, W, H), [contours, W, H]);

  /** Slider: take the N largest objects. Moving right adds detail, which reads the same
   *  way round as "more of the shelf is covered". */
  const setRungAndContours = useCallback((n: number) => {
    setRung(n);
    setContours(ladder.slice(0, Math.max(0, Math.min(n, ladder.length))).map(l => l.contour));
    setSelected(-1);
    setDirty(true);
  }, [ladder]);

  const toCrop = useCallback((e: { clientX: number; clientY: number }): [number, number] => {
    const svg = svgRef.current;
    if (!svg) return [0, 0];
    const r = svg.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
  }, [W, H]);

  const onDown = useCallback((e: React.MouseEvent) => {
    const [x, y] = toCrop(e);
    if (mode === 'pen') {
      setDraft(d => [...d, [Math.round(x), Math.round(y)]]);
      return;
    }
    if (mode !== 'cursor') return;
    if (selected >= 0) {
      const v = nearestVertex(contours[selected], x, y, GRAB_RADIUS);
      if (v >= 0) {
        past.current.push(contours.map(c => c.slice()) as Contour[]);
        future.current = [];
        drag.current = { poly: selected, vertex: v };
        return;
      }
    }
    setSelected(hitTest(contours, x, y));
  }, [mode, contours, selected, toCrop]);

  const onMove = useCallback((e: React.MouseEvent) => {
    if (!drag.current) return;
    const [x, y] = toCrop(e);
    const { poly, vertex } = drag.current;
    setContours(prev => {
      const next = prev.map(c => c.slice()) as Contour[];
      next[poly][vertex] = [Math.round(x), Math.round(y)];
      return next;
    }, false);          // the snapshot was taken when the drag began
    setDirty(true);
  }, [toCrop]);

  const endDrag = useCallback(() => { drag.current = null; }, []);

  const closeDraft = useCallback(() => {
    if (draft.length >= 3) {
      setContours(prev => [...prev, draft]);
      setDirty(true);
    }
    setDraft([]);
  }, [draft]);

  const removeSelected = useCallback(() => {
    if (selected < 0) return;
    setContours(prev => prev.filter((_, i) => i !== selected));
    setSelected(-1);
    setDirty(true);
  }, [selected]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Claim the key, don't just act on it. The percentage grid listens on `document`
        // too, and ITS Delete means "retract this crop's verdict" — so deleting one box
        // used to also un-label the whole crop and schedule that null to the server. The
        // crop then dropped out of the current filter, the grid reflowed, and the page
        // appeared to scroll away under the labeller.
        //
        // Registered in the CAPTURE phase below, so this runs before the grid's
        // bubble-phase listener and stopPropagation actually reaches it. With nothing
        // selected the editor has no box to delete and the key falls through to the grid,
        // which is where Backspace-retracts still belongs.
        if (selected >= 0) { e.preventDefault(); e.stopPropagation(); removeSelected(); }
      } else if (e.key === 'Enter' && mode === 'pen' && draft.length) {
        e.preventDefault(); e.stopPropagation(); closeDraft();
      } else if (e.key === 'Enter') {
        // Enter = "this is right, store it". The pen keeps Enter for closing a contour,
        // but only while one is being drawn — otherwise the commonest key would be taken
        // by the rarer action.
        e.preventDefault();
        // Claimed, like Delete: the grid's own Enter confirms AND steps to the next crop,
        // scrolling after it, so Enter used to save and simultaneously yank the crop off
        // the screen. Enter here means "record this", nothing more — Space in the grid is
        // still the key that walks the queue.
        e.stopPropagation();
        // Saves whether or not anything was edited. Agreeing with SAM's outlines at first
        // sight is the commonest verdict there is, and a button that refuses it makes the
        // fast case the awkward one — the backend credits an unchanged confirmation to
        // whoever gave it, exactly as Space does in the percentage grid.
        if (!saving) void save();
      } else if (e.key === 'z' || e.key === 'я') {
        e.preventDefault(); undo();
      } else if (e.key === 'y' || e.key === 'н') {
        e.preventDefault(); redo();
      } else if (e.key === 's' || e.key === 'ы') {
        e.preventDefault(); splitSelected();
      } else if (e.key === 'Escape') {
        setDraft([]); setSelected(-1);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [selected, mode, removeSelected, closeDraft, draft, dirty, saving, undo, redo,
      splitSelected]);

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      if (apply) {
        await apply.onApply(coverage);
        setDirty(false);
        setSaving(false);
        return;
      }
      const r = await saveShelfMasks(item.id, contours);
      setDirty(false);
      // The SERVER's number, not the one on screen: the browser rasterises small to keep
      // up with a drag, so the two can differ in the last decimal, and the one worth
      // showing after a save is the one that got stored.
      setNote({ ok: true, text: `${t('data.mask.saved')} ${r.data.percentage}%` });
      await mutate();
      onSaved?.(r.data.percentage);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      // Said out loud. The editor used to swallow this entirely, so a failed save looked
      // exactly like a successful one and the only clue was a stale message from the
      // percentage box next door.
      setNote({ ok: false, text: detail || t('data.mask.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const queue = async () => {
    setQueued(true);
    await computeShelfMasks([item.id]);
    await mutate();
  };

  /** Segment the shelf again from a wider view of it, cut fresh from the original frame.
   *
   *  The button exists because re-running the stored crop is a no-op: same pixels, same
   *  prompts, same answer. Refused server-side when the shelf outline has been redrawn
   *  (or the camera re-aimed) since the crop was taken — re-cutting then would segment a
   *  different part of the room and store it over this crop's masks. */
  const recut = async () => {
    recutFrom.current = signature(data);
    setRecutting(true);
    setNote(null);
    try {
      await recutShelfMasks([item.id]);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setRecutting(false);
      setNote({ ok: false, text: detail || t('data.mask.saveFailed') });
    }
  };

  if (isLoading) {
    return <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
    </div>;
  }

  // No masks yet — including every crop in the unlabelled queue. A compact bar rather
  // than an empty editor: the crop is perfectly labellable by the percentage box below,
  // and this is an offer, not a demand.
  if (!data?.computed) {
    return (
      <div className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
        <span className="text-muted-foreground">{t('data.mask.notComputed')}</span>
        <Button size="sm" onClick={queue} disabled={queued}>
          {queued ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  : <Sparkles className="mr-1.5 h-4 w-4" />}
          {queued ? t('data.mask.queued') : t('data.mask.compute')}
        </Button>
        {queued && (
          <span className="text-xs text-muted-foreground">{t('data.mask.queuedHint')}</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {([['view', Eye], ['cursor', MousePointer2], ['pen', PenLine]] as const).map(
          ([m, Icon]) => (
            <Button key={m} size="sm" variant={mode === m ? 'default' : 'outline'}
                    onClick={() => { setMode(m); setDraft([]); }}>
              <Icon className="mr-1.5 h-4 w-4" /> {t(`data.mask.mode.${m}`)}
            </Button>
          ))}
        <Button size="sm" variant="outline" disabled={selected < 0} onClick={removeSelected}>
          <Trash2 className="mr-1.5 h-4 w-4" /> {t('data.mask.delete')}
        </Button>
        <Button size="sm" variant="outline" disabled={!canSplit} onClick={splitSelected}
                title={t('data.mask.splitHint')}>
          <Scissors className="mr-1.5 h-4 w-4" /> {t('data.mask.split')}
        </Button>
        {/* Offered loudly when SAM came back with nothing, quietly otherwise: an empty
            answer is the one case where a labeller has nothing to correct and no other
            move available. */}
        <Button size="sm" variant={objects.length ? 'outline' : 'default'}
                disabled={recutting} onClick={recut} title={t('data.mask.recutHint')}>
          {recutting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                     : <Maximize2 className="mr-1.5 h-4 w-4" />}
          {recutting ? t('data.mask.recutQueued') : t('data.mask.recut')}
        </Button>
        <Button size="sm" variant="ghost" onClick={undo} title="Z">
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={redo} title="Y">
          <Redo2 className="h-4 w-4" />
        </Button>
        <span className="ml-2 text-2xl font-semibold tabular-nums">{coverage}%</span>
        <span className="text-xs text-muted-foreground">
          {t('data.mask.objects')}: {contours.length}
          {raw.length > objects.length &&
            ` (${t('data.mask.deduped').replace('{n}', String(raw.length - objects.length))})`}
          {data.percentage != null && ` · ${t('data.mask.stored')} ${data.percentage}%`}
        </span>
        <Button size="sm" className="ml-auto" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  : <Save className="mr-1.5 h-4 w-4" />}
          {apply ? apply.label : (dirty ? t('data.mask.save') : t('data.mask.confirm'))}
          <kbd className="ml-1.5 rounded bg-white/20 px-1 text-[10px]">Enter</kbd>
        </Button>
        {note && (
          <span className={`text-xs ${note.ok ? 'text-emerald-600' : 'text-destructive'}`}>
            {note.text}
          </span>
        )}
      </div>

      {/* Per-prompt switches, with what each one found. `packaging` on a bakery rack
          paints the liner between the buns; seeing its number next to the tick is what
          makes that obvious instead of mysterious. */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="text-muted-foreground">
          {t('data.mask.base')}: {base.length}
        </span>
        {/* Only when the screen is ACTUALLY empty. It used to fire on an empty base even
            with a prompt ticked and a dozen objects on the crop, which reads as an error
            in the middle of a perfectly good verdict. */}
        {ladder.length === 0 && (
          <span className="text-amber-700">{t('data.mask.emptyBase')}</span>
        )}
        {(data.prompts ?? []).map(p => {
          const n = uniqueTo(objects, p.prompt).length;
          return (
            <label key={p.prompt}
                   className={`inline-flex items-center gap-1.5 ${n ? '' : 'opacity-40'}`}>
              <input
                type="checkbox"
                disabled={!n}
                checked={onPrompts.has(p.prompt)}
                onChange={e => { setDirty(true); setOnPrompts(prev => {
                  const next = new Set(prev);
                  if (e.target.checked) next.add(p.prompt); else next.delete(p.prompt);
                  return next;
                }); }}
              />
              <span className={onPrompts.has(p.prompt) ? '' : 'text-muted-foreground'}>
                {p.prompt}
              </span>
              {/* Only what this prompt adds ON TOP of the base — its own total would be
                  mostly objects already counted, which is the number that misleads. */}
              <span className="text-muted-foreground tabular-nums">+{n}</span>
            </label>
          );
        })}
      </div>

      {/* The ladder. Every object from the ticked prompts, largest first — drag until the
          green matches what is actually on the shelf. */}
      <label className="flex items-center gap-3 text-xs text-muted-foreground">
        {t('data.mask.slider')}
        <input type="range" min={0} max={ladder.length} value={rung} className="flex-1"
               onChange={e => setRungAndContours(Number(e.target.value))} />
        <span className="tabular-nums">{rung} / {ladder.length}</span>
      </label>

      <div className="relative inline-block max-w-full select-none">
        <img src={item.crop_url} alt="" className="block w-full rounded border"
             onError={() => setImgBroken(true)} />
        {imgBroken && (
          <div className="absolute inset-0 flex items-center justify-center rounded border bg-muted/90 p-3 text-center text-xs">
            {t('data.mask.imgExpired')}
          </div>
        )}
        {/* Под объектами: зелёное рисуют поверх, клики слой пропускает. */}
        {showUsefulArea && data.useful_area && <UsefulAreaOverlay area={data.useful_area} />}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className={`absolute inset-0 h-full w-full ${
            mode === 'pen' ? 'cursor-crosshair' : mode === 'cursor' ? 'cursor-pointer' : ''}`}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onDoubleClick={closeDraft}
        >
          {contours.map((c, i) => (
            <polygon
              key={i}
              points={c.map(p => p.join(',')).join(' ')}
              fill={i === selected ? 'rgba(59,130,246,.45)' : 'rgba(34,197,94,.40)'}
              stroke={i === selected ? '#1d4ed8' : '#15803d'}
              strokeWidth={Math.max(2, W / 400)}
            />
          ))}
          {/* Handles only on the selected object: every corner of every object at once is
              a screen full of dots and nothing to aim at. */}
          {selected >= 0 && contours[selected]?.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={Math.max(4, W / 200)}
                    fill="#fff" stroke="#1d4ed8" strokeWidth={Math.max(2, W / 500)} />
          ))}
          {draft.length > 0 && (
            <polyline points={draft.map(p => p.join(',')).join(' ')}
                      fill="rgba(234,179,8,.25)" stroke="#a16207"
                      strokeWidth={Math.max(2, W / 400)} />
          )}
        </svg>
      </div>

      <p className="text-xs text-muted-foreground">
        {mode === 'pen' ? t('data.mask.penHint')
          : mode === 'cursor' ? t('data.mask.cursorHint') : t('data.mask.viewHint')}
        {' · '}{t('data.mask.keysHint')}
      </p>
    </div>
  );
}
